/* =====================================================================
   Bluewud pincode delivery gate.
   Serviceability comes from window.__BW_PIN_DATA (assets/bluewud-pincode-data.js,
   a defer script so it is guaranteed present by the time this runs). Format:
   "pincode|city|state|days\n..." where days already includes the +2 buffer.

   Behaviour (agreed with the merchant; changed by Shubh on 3 Nov 2026 so "Buy it now"
   shows on every product again):
   - Add-to-cart is ALWAYS allowed.
   - Every route to payment is OPEN by default: the cart Checkout button, cart
     accelerated/dynamic buttons, and the product-page "Buy it now" / accelerated
     buttons. It is HARD-blocked only after a visitor checks a pincode we do not
     deliver to, until they check one we do. (Until 3 Nov it was blocked until a
     serviceable pincode was verified, which hid "Buy it now" for every new visitor.)
   - FAIL-OPEN: if the data didn't load, nothing is blocked (a rare stray
     out-of-zone order is cheaper than blocking every customer during an outage).
   - The verified pincode is stored on the device AND written to the cart as an
     order attribute, so the ops team sees it on the order.
   ===================================================================== */
(function () {
  'use strict';
  if (window.BWPin) return;

  var LS_KEY = 'bw:pincode:v1';
  var ATTR_KEY = 'Delivery Pincode';
  var PIN_RE = /^[1-9][0-9]{5}$/;

  var map = null;        // pincode -> { city, state, days }
  var dataFailed = false;

  /* ---- Parse the data once ---- */
  function buildMap() {
    if (map) return map;
    var raw = window.__BW_PIN_DATA;
    if (!raw || typeof raw !== 'string') { dataFailed = true; return null; }
    var m = new Map();
    var lines = raw.split('\n');
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      if (!line) continue;
      var bar = line.indexOf('|');
      var pin = bar === -1 ? line : line.slice(0, bar);
      var rest = bar === -1 ? '' : line.slice(bar + 1);
      var parts = rest.split('|');
      m.set(pin, { city: parts[0] || '', state: parts[1] || '', days: parts[2] || '' });
    }
    if (m.size === 0) { dataFailed = true; return null; }
    map = m;
    return map;
  }

  /* ---- Lookup ---- */
  function check(pin) {
    pin = String(pin || '').trim();
    if (!PIN_RE.test(pin)) return { state: 'invalid' };
    var m = buildMap();
    if (!m) return { state: 'unknown', pin: pin };       // data failed → fail open
    var hit = m.get(pin);
    if (hit) return { state: 'ok', pin: pin, city: hit.city, state2: hit.state, days: hit.days };
    return { state: 'no', pin: pin };                     // valid shape, not serviceable
  }

  /* ---- Stored verified pincode ---- */
  function getStored() {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || 'null'); } catch (e) { return null; }
  }
  function setStored(obj) {
    try { obj ? localStorage.setItem(LS_KEY, JSON.stringify(obj)) : localStorage.removeItem(LS_KEY); } catch (e) {}
  }

  /* Open unless the stored pincode is one we do not deliver to. Data absent → allow. */
  function isAllowed() {
    if (dataFailed || !window.__BW_PIN_DATA) return true;   // fail-open
    var s = getStored();
    return !(s && s.blocked);
  }
  /* What a stored pincode shows in the checker: delivers, or not yet. */
  function storedResult(s) {
    return s.blocked ? { state: 'no', pin: s.pin } : { state: 'ok', pin: s.pin, city: s.city, state2: s.state2, days: s.days };
  }

  /* ---- Write the pincode onto the cart (shows on the order) ---- */
  function saveToCart(res) {
    var attrs = {};
    attrs[ATTR_KEY] = (res && res.state === 'ok')
      ? (res.pin + (res.city ? ' — ' + res.city + ', ' + res.state2 : ''))
      : '';
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
        credentials: 'same-origin'
      });
    } catch (e) {}
  }

  /* ---- Gate every checkout path ---- */
  function checkoutButtons() {
    return [].slice.call(document.querySelectorAll(
      'button[name="checkout"], input[name="checkout"], [data-bw-checkout]'
    ));
  }
  function dynamicButtons() {
    /* Accelerated / dynamic checkout (Shop Pay, GPay, etc.) — these skip the
       cart, so hiding them is the reliable block. */
    return [].slice.call(document.querySelectorAll(
      '.shopify-payment-button, shopify-payment-button, .additional-checkout-buttons, ' +
      '[data-shopify="payment-button"], .dynamic-checkout__content, .product-form__buy-now'
    ));
  }

  function applyGate() {
    var allow = isAllowed();
    checkoutButtons().forEach(function (b) {
      /* NATIVE disable is the real hard block — a disabled submit button
         cannot submit a form or be clicked, with no dependency on JS timing.
         The class is only the visual. We remember whether WE disabled it so we
         never re-enable a button the theme disabled for its own reasons. */
      if (!allow) {
        if (!b.disabled) { b.dataset.bwDisabled = '1'; b.disabled = true; }
        b.classList.add('bw-pin-blocked');
        b.setAttribute('aria-disabled', 'true');
      } else {
        if (b.dataset.bwDisabled === '1') { b.disabled = false; delete b.dataset.bwDisabled; }
        b.classList.remove('bw-pin-blocked');
        b.removeAttribute('aria-disabled');
      }
    });
    dynamicButtons().forEach(function (d) {
      if (!allow) { if (d.dataset.bwHidden !== '1') { d.dataset.bwPrevDisplay = d.style.display; d.dataset.bwHidden = '1'; d.style.display = 'none'; } }
      else if (d.dataset.bwHidden === '1') { d.style.display = d.dataset.bwPrevDisplay || ''; delete d.dataset.bwHidden; delete d.dataset.bwPrevDisplay; }
    });
    document.querySelectorAll('[data-bw-pin-gatehint]').forEach(function (h) { h.hidden = allow; });
  }

  function flashCheckers() {
    document.querySelectorAll('[data-bw-pin-card]').forEach(function (c) {
      c.classList.remove('bw-pin-card--flash');
      /* reflow to restart the animation */
      void c.offsetWidth;
      c.classList.add('bw-pin-card--flash');
    });
    var first = document.querySelector('[data-bw-pin-input]');
    if (first) { try { first.scrollIntoView({ behavior: 'smooth', block: 'center' }); first.focus({ preventScroll: true }); } catch (e) {} }
  }

  /* Capture-phase interceptors — even if a button is re-enabled by other JS,
     a gated checkout can't actually submit. Add-to-cart is never touched. */
  function installInterceptors() {
    document.addEventListener('click', function (e) {
      if (isAllowed()) return;
      var t = e.target.closest && e.target.closest(
        'button[name="checkout"], input[name="checkout"], [data-bw-checkout], ' +
        '.shopify-payment-button, shopify-payment-button'
      );
      if (t) { e.preventDefault(); e.stopPropagation(); flashCheckers(); }
    }, true);

    document.addEventListener('submit', function (e) {
      if (isAllowed()) return;
      var sub = e.submitter;
      var isCheckout = sub && (sub.name === 'checkout' || (sub.hasAttribute && sub.hasAttribute('data-bw-checkout')));
      if (isCheckout) { e.preventDefault(); e.stopPropagation(); flashCheckers(); }
    }, true);
  }

  /* ---- Checker UI (mounted where Liquid placed [data-bw-pin-mount]) ---- */
  function renderResult(el, res) {
    if (!res || res.state === 'idle') { el.hidden = true; el.textContent = ''; return; }
    el.hidden = false;
    el.dataset.state = res.state;
    if (res.state === 'ok') {
      var unit = (String(res.days) === '1') ? ' day' : ' days';
      el.innerHTML = '<strong>✓ Delivers to ' + esc(res.city) + ', ' + esc(res.state2) + '</strong> — approx ' + esc(res.days) + unit;
    } else if (res.state === 'no') {
      el.innerHTML = '<strong>Sorry — we don’t deliver to ' + esc(res.pin) + ' yet.</strong> Add to cart and we’ll notify you, or try another pincode.';
    } else if (res.state === 'invalid') {
      el.textContent = 'Enter a valid 6-digit pincode.';
    } else if (res.state === 'unknown') {
      el.textContent = ''; el.hidden = true;   // data unavailable → say nothing, fail open
    }
  }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  function wireChecker(mount) {
    if (mount.dataset.bwPinReady === '1') return;
    mount.dataset.bwPinReady = '1';

    /* NO nested <form> — this mount lives inside the cart/product <form>, and
       HTML forbids nested forms (the parser silently drops the inner tag). Use
       a plain div + a type="button" and wire click + Enter ourselves. */
    mount.innerHTML =
      '<div class="bw-pin-card" data-bw-pin-card>' +
        '<div class="bw-pin-card__label">Check delivery to your pincode</div>' +
        '<div class="bw-pin-card__row">' +
          '<input type="text" inputmode="numeric" autocomplete="postal-code" maxlength="6" ' +
                 'placeholder="Enter 6-digit pincode" aria-label="Delivery pincode" data-bw-pin-input>' +
          '<button type="button" class="bw-pin-card__btn" data-bw-pin-check>Check</button>' +
        '</div>' +
        '<p class="bw-pin-card__result" data-bw-pin-result hidden></p>' +
      '</div>';

    var input = mount.querySelector('[data-bw-pin-input]');
    var checkBtn = mount.querySelector('[data-bw-pin-check]');
    var result = mount.querySelector('[data-bw-pin-result]');

    input.addEventListener('input', function () {
      input.value = input.value.replace(/\D/g, '').slice(0, 6);
    });

    function run() {
      var res = check(input.value);
      renderResult(result, res);
      if (res.state === 'ok') {
        setStored({ pin: res.pin, city: res.city, state2: res.state2, days: res.days });
        saveToCart(res);
      } else if (res.state === 'no') {
        setStored({ pin: res.pin, blocked: true });          // payment stays blocked until a pincode we deliver to
        saveToCart(res);
      }
      syncAll();
      applyGate();
    }

    checkBtn.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); run(); });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); run(); }
    });

    /* prefill from a previously checked pincode */
    var s = getStored();
    if (s && s.pin) {
      input.value = s.pin;
      renderResult(result, storedResult(s));
    }
  }

  /* Keep every mounted checker (product + cart) showing the same state. */
  function syncAll() {
    var s = getStored();
    document.querySelectorAll('[data-bw-pin-mount]').forEach(function (mount) {
      var input = mount.querySelector('[data-bw-pin-input]');
      var result = mount.querySelector('[data-bw-pin-result]');
      if (!input || !result) return;
      if (s && s.pin) {
        if (input.value !== s.pin) input.value = s.pin;
        renderResult(result, storedResult(s));
      }
    });
  }

  function mountAll() {
    document.querySelectorAll('[data-bw-pin-mount]').forEach(wireChecker);
  }

  function safe(fn) { try { fn(); } catch (e) { /* one failed phase never disables the gate */ } }

  function start() {
    /* Interceptors FIRST and unconditionally — they are the instant safety net,
       so they must exist even if data parsing or UI mounting throws. */
    safe(buildMap);
    safe(applyGate);
    safe(mountAll);

    /* Cart re-renders and dynamic checkout buttons arrive late. Re-gate on DOM
       changes via a setTimeout debounce (NOT requestAnimationFrame — rAF is
       throttled in background tabs / some webviews, which would leave the gate
       un-applied after a re-render). */
    if ('MutationObserver' in window) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        setTimeout(function () { pending = false; safe(mountAll); safe(applyGate); }, 60);
      }).observe(document.documentElement, { childList: true, subtree: true });
    }

    /* Low-frequency safety net: even if an observer edge case or a theme script
       re-enables the button, this re-applies within a second. Cheap insurance
       for a checkout-critical gate. */
    setInterval(function () { safe(applyGate); }, 1000);

    window.addEventListener('storage', function (e) { if (e.key === LS_KEY) { safe(syncAll); safe(applyGate); } });
    window.addEventListener('pageshow', function () { safe(applyGate); });
  }

  /* Install the capture-phase interceptors at script-execution time (defer, so
     the DOM is parsed). Independent of start(), so they are always active. */
  installInterceptors();

  window.BWPin = { check: check, isAllowed: isAllowed, applyGate: applyGate };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
